import React, { useState } from 'react';
import { Check, AlertCircle, Eye, EyeOff, LucideIcon } from 'lucide-react';

interface FormFieldProps {
  label: string;
  name: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  type?: 'text' | 'email' | 'tel' | 'password' | 'number' | 'date' | 'time';
  placeholder?: string;
  icon?: LucideIcon;
  required?: boolean;
  error?: string;
  hint?: string;
  touched?: boolean;
  valid?: boolean;
  disabled?: boolean;
  className?: string;
  multiline?: boolean;
  rows?: number;
}

const FormField: React.FC<FormFieldProps> = ({
  label,
  name,
  value,
  onChange,
  onBlur,
  type = 'text',
  placeholder,
  icon: Icon,
  required,
  error,
  hint,
  touched,
  valid,
  disabled,
  className = '',
  multiline,
  rows = 3,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [focused, setFocused] = useState(false);

  const hasError = touched && error;
  const isValid = touched && !error && valid && value.length > 0;
  const isPassword = type === 'password';
  const actualType = isPassword ? (showPassword ? 'text' : 'password') : type;

  const borderClass = hasError
    ? 'border-red-300 focus:border-red-500 focus:ring-red-500/20 bg-red-50/30'
    : isValid
    ? 'border-emerald-300 focus:border-emerald-500 focus:ring-emerald-500/20 bg-emerald-50/30'
    : focused
    ? 'border-teal-300 focus:border-teal-500 focus:ring-teal-500/20'
    : 'border-gray-200 focus:border-teal-400 focus:ring-teal-500/20';

  const InputElement = multiline ? 'textarea' : 'input';

  return (
    <div className={`space-y-1.5 ${className}`}>
      {/* Label */}
      <label htmlFor={name} className="block text-sm font-semibold text-gray-700">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>

      {/* Input wrapper */}
      <div className="relative">
        {Icon && (
          <Icon
            className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 transition-colors ${
              hasError ? 'text-red-400' : isValid ? 'text-emerald-500' : 'text-gray-400'
            }`}
          />
        )}

        <InputElement
          id={name}
          name={name}
          type={actualType}
          value={value}
          onChange={(e: any) => onChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => {
            setFocused(false);
            onBlur?.();
          }}
          placeholder={placeholder}
          disabled={disabled}
          rows={multiline ? rows : undefined}
          className={`
            w-full rounded-xl border-2 px-3 py-2.5 text-sm
            ${Icon ? 'pl-9' : ''}
            ${(isPassword || isValid || hasError) ? 'pr-10' : ''}
            ${multiline ? 'resize-none' : ''}
            transition-all duration-200
            outline-none focus:ring-4
            ${borderClass}
            ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}
            text-gray-900 placeholder:text-gray-400
          `}
        />

        {/* Right icons */}
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {isPassword && (
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="text-gray-400 hover:text-gray-600 transition"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          )}

          {!isPassword && isValid && (
            <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center animate-in fade-in zoom-in duration-200">
              <Check className="w-3 h-3 text-white" strokeWidth={3} />
            </div>
          )}

          {!isPassword && hasError && (
            <AlertCircle className="w-5 h-5 text-red-500 animate-in fade-in zoom-in duration-200" />
          )}
        </div>
      </div>

      {/* Hata / İpucu */}
      <div className="min-h-[18px]">
        {hasError && (
          <p className="text-xs text-red-600 font-medium flex items-center gap-1 animate-in slide-in-from-top-1 duration-150">
            <AlertCircle className="w-3 h-3 shrink-0" />
            {error}
          </p>
        )}
        {!hasError && hint && (
          <p className="text-xs text-gray-500">{hint}</p>
        )}
      </div>
    </div>
  );
};

export default FormField;
